import React, { useEffect, useRef, useCallback } from 'react';

/*
 * ConstructionCanvas — Full-page animated construction vector art
 * Drawn on HTML5 Canvas. Purely additive, pointer-events:none.
 *
 * Variants control which systems run:
 *   'hero'      — grid + cranes + beams (full, dramatic)
 *   'blueprint' — grid + measurement marks + floating shapes
 *   'structure' — beams + measurement marks
 *   'crane'     — grid + cranes
 *   'light'     — subtle floating shapes + grid (for light content)
 */

const TAU = Math.PI * 2;

// ── Colour helpers ────────────────────────────────────────────
function hex2rgb(h) {
  const c = h.replace('#', '');
  return [parseInt(c.slice(0,2),16), parseInt(c.slice(2,4),16), parseInt(c.slice(4,6),16)];
}
function rgba(hex, a) {
  const [r,g,b] = hex2rgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}

// ── 1. Blueprint Grid ─────────────────────────────────────────
class BlueprintGrid {
  constructor(W, H, col) {
    this.W = W; this.H = H; this.col = col;
    const sp = 55;
    this.vLines = []; this.hLines = [];
    this.nodes = []; this.pulses = [];
    for (let x = 0; x <= W + sp; x += sp) this.vLines.push(x);
    for (let y = 0; y <= H + sp; y += sp) this.hLines.push(y);
    for (let x = 0; x <= W; x += sp)
      for (let y = 0; y <= H; y += sp)
        if (Math.random() < 0.12)
          this.nodes.push({ x, y, phase: Math.random()*TAU, r: 2+Math.random()*2 });
    for (let i = 0; i < 5; i++)
      this.pulses.push({ x: Math.random()*W, y: Math.random()*H, r: 0, maxR: 50+Math.random()*70, spd: 0.35+Math.random()*0.25 });
  }
  draw(ctx, t) {
    // Grid lines — animated flowing dash offset
    ctx.save();
    ctx.strokeStyle = rgba(this.col, 0.14);
    ctx.lineWidth = 1;
    ctx.setLineDash([12, 8]);
    ctx.lineDashOffset = -(t * 15) % 20;
    this.vLines.forEach(x => { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,this.H); ctx.stroke(); });
    ctx.lineDashOffset = -(t * 10) % 20;
    this.hLines.forEach(y => { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(this.W,y); ctx.stroke(); });
    ctx.setLineDash([]);

    // Nodes + crosshairs
    this.nodes.forEach(n => {
      const p = 0.5 + 0.5*Math.sin(t*1.1 + n.phase);
      ctx.globalAlpha = 0.18 + 0.18*p;
      ctx.fillStyle = this.col;
      ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, TAU); ctx.fill();
      ctx.globalAlpha = 0.08 + 0.06*p;
      ctx.strokeStyle = this.col; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(n.x-10,n.y); ctx.lineTo(n.x+10,n.y);
      ctx.moveTo(n.x,n.y-10); ctx.lineTo(n.x,n.y+10); ctx.stroke();
    });

    // Sonar pulses
    this.pulses.forEach(p => {
      p.r += p.spd;
      if (p.r > p.maxR) { p.r=0; p.x=Math.random()*this.W; p.y=Math.random()*this.H; }
      ctx.globalAlpha = 0.22*(1-p.r/p.maxR);
      ctx.strokeStyle = this.col; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, TAU); ctx.stroke();
    });
    ctx.restore();
  }
}

// ── 2. Structural I-Beams ─────────────────────────────────────
class StructuralBeams {
  constructor(W, H, col) {
    this.W=W; this.H=H; this.col=col;
    this.beams=[]; this.braces=[]; this.sparks=[];
    const n = Math.max(2, Math.floor(W/200));
    const xs = Array.from({length:n}, (_,i) => W/(n+1)*(i+1) + (Math.random()-.5)*60);
    xs.forEach(x => {
      this.beams.push({
        x, topY: H*0.04+Math.random()*H*0.12, botY: H*0.72+Math.random()*H*0.22,
        fw: 12+Math.random()*10, ww: 3+Math.random()*2,
        prog: Math.random()*0.4, spd: 0.003+Math.random()*0.002,
        sway: 0, swayPh: Math.random()*TAU,
        alpha: 0.22, fadingOut: false, holdT: 0,
      });
    });
    // diagonal braces between adjacent beams
    for (let i=0; i<xs.length-1; i++) {
      this.braces.push({
        x1:xs[i], y1:H*0.25+Math.random()*H*0.1,
        x2:xs[i+1], y2:H*0.55+Math.random()*H*0.1,
        prog:0, spd:0.0015,
      });
    }
    for (let i=0; i<16; i++)
      this.sparks.push({ x:Math.random()*W, y:Math.random()*H, vx:(Math.random()-.5)*.5, vy:(Math.random()-.5)*.5, life:Math.random(), max:0.5+Math.random()*0.7, r:1+Math.random()*1.5 });
  }
  draw(ctx, t) {
    ctx.save();
    // Beams — build → hold → fade → reset cycle across full canvas
    this.beams.forEach(b => {
      b.sway = Math.sin(t*0.35+b.swayPh)*1.2;
      if (b.fadingOut) {
        b.alpha = Math.max(0, b.alpha - 0.005);
        if (b.alpha <= 0) {
          b.prog = 0; b.alpha = 0.22; b.fadingOut = false; b.holdT = 0;
          b.x = Math.random() * this.W;
          b.topY = this.H*0.04 + Math.random()*this.H*0.12;
          b.botY = this.H*0.72 + Math.random()*this.H*0.22;
          b.swayPh = Math.random()*TAU;
          return; // skip drawing this reset frame
        }
      } else {
        b.prog = Math.min(1, b.prog + b.spd);
        if (b.prog >= 1) { b.holdT = (b.holdT || 0) + 0.016; if (b.holdT > 2.5) b.fadingOut = true; }
      }
      const x = b.x + b.sway;
      const h = (b.botY-b.topY)*b.prog;
      ctx.globalAlpha = b.alpha;
      ctx.fillStyle = this.col;
      ctx.fillRect(x-b.ww/2, b.topY, b.ww, h);          // web
      if (b.prog>0.04) ctx.fillRect(x-b.fw/2, b.topY, b.fw, b.ww); // top flange
      if (b.prog>0.96) ctx.fillRect(x-b.fw/2, b.topY+h-b.ww, b.fw, b.ww); // bot flange
      // bolt dots
      ctx.globalAlpha = b.alpha * 0.6; ctx.fillStyle='#fff';
      for (let bm=0.12; bm<b.prog; bm+=0.18) {
        ctx.beginPath(); ctx.arc(x, b.topY+h*bm, 1.5, 0, TAU); ctx.fill();
      }
    });
    // Braces
    this.braces.forEach(br => {
      br.prog = Math.min(1, br.prog+br.spd);
      ctx.globalAlpha = 0.13;
      ctx.strokeStyle = this.col; ctx.lineWidth=2.2; ctx.setLineDash([5,4]);
      ctx.beginPath();
      ctx.moveTo(br.x1, br.y1);
      ctx.lineTo(br.x1+(br.x2-br.x1)*br.prog, br.y1+(br.y2-br.y1)*br.prog);
      ctx.stroke(); ctx.setLineDash([]);
    });
    // Sparks
    this.sparks.forEach(s => {
      s.x+=s.vx; s.y+=s.vy; s.life+=0.007;
      if (s.life>s.max) { s.x=Math.random()*this.W; s.y=Math.random()*this.H; s.life=0; s.vx=(Math.random()-.5)*.5; s.vy=(Math.random()-.5)*.5; }
      const a = Math.sin((s.life/s.max)*Math.PI);
      ctx.globalAlpha = a*0.45; ctx.fillStyle=this.col;
      ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, TAU); ctx.fill();
    });
    ctx.restore();
  }
}

// ── 3. Tower Cranes ───────────────────────────────────────────
class TowerCranes {
  constructor(W, H, col) {
    this.W=W; this.H=H; this.col=col;
    this.cranes=[];
    const n = Math.max(1, Math.floor(W/420));
    for (let i=0; i<n; i++) {
      const bx = W/(n+1)*(i+1) + (Math.random()-.5)*80;
      this.cranes.push({
        bx, by:H*0.96,
        h: H*(0.42+Math.random()*0.22),
        arm: 70+Math.random()*90,
        counter: 28+Math.random()*24,
        hookPct: 0.3, hookDir:1, hookSpd:0.0006+Math.random()*0.0004,
        trolleyPct: 0.4, trolleyDir:1, trolleySpd:0.0005+Math.random()*0.0004,
        swayPh:Math.random()*TAU, prog:0, spd:0.003+Math.random()*0.002,
      });
    }
  }
  draw(ctx, t) {
    ctx.save();
    this.cranes.forEach(cr => {
      cr.prog = Math.min(1, cr.prog+cr.spd);
      cr.hookPct += cr.hookSpd*cr.hookDir; if (cr.hookPct>0.85||cr.hookPct<0.1) cr.hookDir*=-1;
      cr.trolleyPct += cr.trolleySpd*cr.trolleyDir; if (cr.trolleyPct>0.9||cr.trolleyPct<0.1) cr.trolleyDir*=-1;
      const sw = Math.sin(t*0.4+cr.swayPh)*1.4;
      const tx = cr.bx+sw, ty = cr.by - cr.h*cr.prog;

      const crPulse = 0.17 + 0.07*Math.sin(t*0.9 + cr.swayPh);
      ctx.globalAlpha = crPulse;
      ctx.strokeStyle = this.col; ctx.lineWidth=4.5;
      // Mast
      ctx.beginPath(); ctx.moveTo(cr.bx, cr.by); ctx.lineTo(tx, ty); ctx.stroke();
      if (cr.prog<0.55) return;
      // Jib
      ctx.lineWidth=3.5;
      ctx.beginPath(); ctx.moveTo(tx,ty); ctx.lineTo(tx+cr.arm*(cr.prog-.5)/0.5, ty-3); ctx.stroke();
      // Counter jib
      ctx.lineWidth=3;
      ctx.beginPath(); ctx.moveTo(tx,ty); ctx.lineTo(tx-cr.counter,ty); ctx.stroke();
      if (cr.prog<0.85) return;
      // Trolley
      const trX = tx + cr.arm*cr.trolleyPct;
      ctx.globalAlpha=0.28; ctx.fillStyle=this.col;
      ctx.fillRect(trX-4, ty-9, 8, 7);
      // Rope
      const ropeY = ty + cr.h*cr.hookPct*0.65;
      ctx.globalAlpha=0.15; ctx.lineWidth=1.8; ctx.setLineDash([3,3]);
      ctx.beginPath(); ctx.moveTo(trX,ty); ctx.lineTo(trX,ropeY); ctx.stroke();
      ctx.setLineDash([]);
      // Hook
      ctx.globalAlpha=0.28; ctx.lineWidth=2.6;
      ctx.beginPath(); ctx.arc(trX, ropeY+5, 5, 0.4, Math.PI+0.4); ctx.stroke();
      // Mast bracing
      ctx.globalAlpha=0.1; ctx.lineWidth=1.5;
      const brN = Math.floor(cr.h*cr.prog/38);
      for (let i=0; i<brN; i++) {
        const y = cr.by-38*(i+0.5);
        ctx.beginPath(); ctx.moveTo(cr.bx-8,y+19); ctx.lineTo(cr.bx,y); ctx.lineTo(cr.bx+8,y+19); ctx.stroke();
      }
    });
    ctx.restore();
  }
}

// ── 4. Dimension / Measurement marks ─────────────────────────
class DimensionLines {
  constructor(W, H, col) {
    this.W=W; this.H=H; this.col=col;
    this.ticks=[]; this.dims=[];
    const sp=40;
    for (let x=sp; x<W-sp; x+=sp)
      this.ticks.push({ x, big: x%160===0, phase: Math.random()*TAU });
    for (let y=sp; y<H-sp; y+=sp)
      this.ticks.push({ y, big: y%160===0, phase: Math.random()*TAU });
    const dc = Math.max(5, Math.floor(W/180));
    for (let i=0; i<dc; i++) {
      const x1=40+Math.random()*(W-120), y=20+Math.random()*(H-40);
      const len=60+Math.random()*140;
      this.dims.push({ x1, y, x2:x1+len, label:`${Math.round(len/2)}m`, ph:Math.random()*TAU });
    }
  }
  draw(ctx, t) {
    ctx.save();
    ctx.strokeStyle=this.col; ctx.fillStyle=this.col; ctx.font='7px monospace';
    // Ruler ticks — individually pulsing alpha
    ctx.lineWidth=1.4;
    this.ticks.forEach(tk => {
      const pulse = 0.5 + 0.5*Math.sin(t*1.8 + tk.phase);
      ctx.globalAlpha = 0.06 + 0.12*pulse;
      if (tk.x!==undefined) {
        ctx.beginPath(); ctx.moveTo(tk.x,this.H-18); ctx.lineTo(tk.x,this.H-(tk.big?13:9)); ctx.stroke();
        if (tk.big) { ctx.globalAlpha=0.08+0.1*pulse; ctx.fillText(String(Math.round(tk.x/4)),tk.x-5,this.H-2); }
      } else {
        ctx.beginPath(); ctx.moveTo(18,tk.y); ctx.lineTo(tk.big?13:9,tk.y); ctx.stroke();
      }
    });
    // Dimension lines
    this.dims.forEach(d => {
      const pulse = 0.6+0.4*Math.sin(t*0.7+d.ph);
      ctx.globalAlpha=0.18*pulse; ctx.lineWidth=1.6;
      ctx.strokeStyle=this.col;
      ctx.beginPath(); ctx.moveTo(d.x1,d.y); ctx.lineTo(d.x2,d.y); ctx.stroke();
      [-4,4].forEach(dy => {
        ctx.beginPath(); ctx.moveTo(d.x1,d.y+dy); ctx.lineTo(d.x1,d.y-dy); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(d.x2,d.y+dy); ctx.lineTo(d.x2,d.y-dy); ctx.stroke();
      });
      ctx.globalAlpha=0.2*pulse;
      ctx.fillStyle=this.col; ctx.fillText(d.label,(d.x1+d.x2)/2-8,d.y-5);
    });
    ctx.restore();
  }
}

// ── 5. Floating blueprint shapes ─────────────────────────────
class FloatingShapes {
  constructor(W, H, col) {
    this.W=W; this.H=H; this.col=col; this.shapes=[];
    const TYPES=['rect','L','T','cross','I'];
    for (let i=0; i<10; i++) {
      this.shapes.push({
        x:Math.random()*W, y:Math.random()*H,
        type:TYPES[i%TYPES.length],
        sc:12+Math.random()*26,
        angle:Math.random()*TAU,
        rot:(Math.random()-.5)*0.003,
        vx:(Math.random()-.5)*0.12,
        vy:-0.06-Math.random()*0.1,
        alpha:0.06+Math.random()*0.1,
      });
    }
  }
  draw(ctx, t) {
    this.shapes.forEach(s => {
      s.x+=s.vx; s.y+=s.vy; s.angle+=s.rot;
      if (s.y<-60) s.y=this.H+40;
      if (s.x<-60) s.x=this.W+40;
      if (s.x>this.W+60) s.x=-40;
      ctx.save();
      ctx.translate(s.x,s.y); ctx.rotate(s.angle);
      ctx.globalAlpha=s.alpha; ctx.strokeStyle=this.col; ctx.lineWidth=2.2;
      const sc=s.sc;
      ctx.beginPath();
      switch(s.type){
        case 'rect': ctx.strokeRect(-sc*.5,-sc*.3,sc,sc*.6); ctx.moveTo(-sc*.5,0); ctx.lineTo(sc*.5,0); break;
        case 'L':    ctx.moveTo(-sc*.4,-sc*.4); ctx.lineTo(-sc*.4,sc*.4); ctx.lineTo(sc*.4,sc*.4); break;
        case 'T':    ctx.moveTo(-sc*.5,-sc*.3); ctx.lineTo(sc*.5,-sc*.3); ctx.moveTo(0,-sc*.3); ctx.lineTo(0,sc*.4); break;
        case 'cross':ctx.moveTo(-sc*.5,0); ctx.lineTo(sc*.5,0); ctx.moveTo(0,-sc*.5); ctx.lineTo(0,sc*.5); break;
        case 'I':    ctx.moveTo(-sc*.4,-sc*.35); ctx.lineTo(sc*.4,-sc*.35); ctx.moveTo(0,-sc*.35); ctx.lineTo(0,sc*.35); ctx.moveTo(-sc*.4,sc*.35); ctx.lineTo(sc*.4,sc*.35); break;
      }
      ctx.stroke();
      ctx.restore();
    });
  }
}

// ── 6. Excavator silhouette (bonus, subtle) ───────────────────
class Excavator {
  constructor(W, H, col) {
    this.W=W; this.H=H; this.col=col;
    this.x=W*0.08; this.y=H*0.92; this.phase=0; this.spd=0.008;
    this.armAngle=-0.4;
  }
  draw(ctx, t) {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.globalAlpha=0.12; ctx.strokeStyle=this.col; ctx.lineWidth=1.8; ctx.fillStyle=this.col;

    // tracks
    ctx.globalAlpha=0.1;
    ctx.fillRect(-30,0,60,10); ctx.fillRect(-28,10,56,6);

    // body
    ctx.globalAlpha=0.12;
    ctx.fillRect(-20,-22,40,22);

    // cabin
    ctx.fillRect(-10,-36,22,14);

    // boom arm (animated)
    const boom = -0.6 + 0.15*Math.sin(t*0.6);
    ctx.save(); ctx.translate(10,-22);
    ctx.rotate(boom);
    ctx.fillRect(0,-6,50,6); // boom
    // stick
    const stick = 0.5+0.2*Math.sin(t*0.6+1);
    ctx.save(); ctx.translate(50,-3); ctx.rotate(stick);
    ctx.fillRect(0,-4,34,4);
    // bucket
    ctx.save(); ctx.translate(34,-2);
    ctx.rotate(-0.6+0.3*Math.sin(t*0.6+2));
    ctx.beginPath(); ctx.arc(0,0,9,0,Math.PI); ctx.fill();
    ctx.restore(); ctx.restore(); ctx.restore();

    ctx.restore();
  }
}

// ── 7. Flowing self-drawing line segments — full-canvas coverage ─
class FlowingLines {
  constructor(W, H, col) {
    this.W=W; this.H=H; this.col=col; this.lines=[];
    for (let i=0; i<14; i++) this.lines.push(this._make());
  }
  _make() {
    const angle = (Math.random()-0.5)*Math.PI*1.4;
    const len   = 55 + Math.random()*130;
    return {
      x: Math.random()*this.W, y: Math.random()*this.H,
      angle, len,
      prog: Math.random()*2,            // stagger so lines appear everywhere from the start
      spd:  0.004 + Math.random()*0.007,
      baseAlpha: 0.12 + Math.random()*0.14,
    };
  }
  draw(ctx, t) {
    this.lines.forEach(l => {
      l.prog += l.spd;
      let drawn, alpha;
      if      (l.prog < 1)   { drawn = l.prog; alpha = l.baseAlpha * l.prog; }
      else if (l.prog < 1.8) { drawn = 1;      alpha = l.baseAlpha; }
      else if (l.prog < 2.6) { drawn = 1;      alpha = l.baseAlpha * (1-(l.prog-1.8)/0.8); }
      else { Object.assign(l, this._make()); l.prog = 0; return; }
      const ex = l.x + Math.cos(l.angle)*l.len*drawn;
      const ey = l.y + Math.sin(l.angle)*l.len*drawn;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = this.col; ctx.lineWidth = 1.8;
      ctx.beginPath(); ctx.moveTo(l.x,l.y); ctx.lineTo(ex,ey); ctx.stroke();
      if (drawn > 0.92) {   // endpoint tick marks once line is nearly complete
        const px = Math.sin(l.angle)*5, py = -Math.cos(l.angle)*5;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(l.x-px,l.y-py); ctx.lineTo(l.x+px,l.y+py);
        ctx.moveTo(ex-px,ey-py);   ctx.lineTo(ex+px,ey+py);
        ctx.stroke();
      }
      ctx.restore();
    });
  }
}

// ── Main component ────────────────────────────────────────────
const VARIANTS = {
  hero:      ['BlueprintGrid','StructuralBeams','TowerCranes','DimensionLines','FloatingShapes','FlowingLines'],
  blueprint: ['BlueprintGrid','DimensionLines','FloatingShapes','FlowingLines'],
  structure: ['StructuralBeams','DimensionLines','FlowingLines'],
  crane:     ['BlueprintGrid','TowerCranes','FlowingLines'],
  full:      ['BlueprintGrid','StructuralBeams','TowerCranes','DimensionLines','FloatingShapes','Excavator','FlowingLines'],
  light:     ['BlueprintGrid','FloatingShapes','FlowingLines'],
  minimal:   ['BlueprintGrid','FlowingLines'],
};

const BUILDERS = {
  BlueprintGrid:  (W,H,col) => new BlueprintGrid(W,H,col),
  StructuralBeams:(W,H,col) => new StructuralBeams(W,H,col),
  TowerCranes:    (W,H,col) => new TowerCranes(W,H,col),
  DimensionLines: (W,H,col) => new DimensionLines(W,H,col),
  FloatingShapes: (W,H,col) => new FloatingShapes(W,H,col),
  Excavator:      (W,H,col) => new Excavator(W,H,col),
  FlowingLines:   (W,H,col) => new FlowingLines(W,H,col),
};

export default function ConstructionCanvas({
  variant = 'blueprint',
  color   = '#B4D3D9',   // single line color — caller picks what contrasts
  opacity = 1,
  style   = {},
}) {
  const canvasRef = useRef(null);
  const stRef     = useRef({ systems:[], raf:null, t:0 });

  const build = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const W = cv.offsetWidth, H = cv.offsetHeight;
    if (!W||!H) return;
    cv.width=W; cv.height=H;
    const names = VARIANTS[variant] || VARIANTS.blueprint;
    stRef.current.systems = names.map(n => BUILDERS[n](W,H,color));
  }, [variant, color]);

  const loop = useCallback(() => {
    const cv  = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext('2d');
    const st  = stRef.current;
    st.t += 0.016;
    ctx.clearRect(0,0,cv.width,cv.height);
    st.systems.forEach(sys => { ctx.save(); sys.draw(ctx,st.t); ctx.restore(); });
    ctx.globalAlpha=1;
    st.raf = requestAnimationFrame(loop);
  }, []);

  useEffect(() => {
    build();
    stRef.current.raf = requestAnimationFrame(loop);
    const ro = new ResizeObserver(build);
    const parent = canvasRef.current?.parentElement;
    if (parent) ro.observe(parent);
    return () => { if (stRef.current.raf) cancelAnimationFrame(stRef.current.raf); ro.disconnect(); };
  }, [build, loop]);

  return (
    <canvas ref={canvasRef} style={{
      position:'absolute', inset:0, width:'100%', height:'100%',
      opacity, pointerEvents:'none', display:'block', ...style,
    }} />
  );
}
